import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
    static values = { disabled: Boolean };

    disabledValueChanged(disabled: boolean): void {
        this.element.inert = disabled;
    }

    static targets = ["display", "editor", "input", "assignee", "assigneeIds", "signatureFrame", "signatureSource"];

    declare readonly signatureFrameTarget: HTMLIFrameElement;
    declare readonly hasSignatureFrameTarget: boolean;
    declare readonly signatureSourceTargets: HTMLTemplateElement[];
    declare readonly displayTarget: HTMLElement;
    declare readonly editorTarget: HTMLElement;
    declare readonly inputTarget: HTMLInputElement | HTMLTextAreaElement;
    declare readonly assigneeTargets: HTMLInputElement[];
    declare readonly assigneeIdsTarget: HTMLInputElement;
    declare readonly hasDisplayTarget: boolean;
    declare readonly hasEditorTarget: boolean;
    declare readonly hasInputTarget: boolean;
    declare readonly hasAssigneeIdsTarget: boolean;

    private readonly syncAssigneePopoverAfterClick = (event: MouseEvent): void => {
        this.syncAssigneePopoverState(event);
    };

    connect(): void {
        const trigger = this.assigneeDropdown()?.querySelector<HTMLElement>(":scope > button");
        if (trigger) {
            document.addEventListener("click", this.syncAssigneePopoverAfterClick, true);
        }
    }

    disconnect(): void {
        document.removeEventListener("click", this.syncAssigneePopoverAfterClick, true);
    }

    edit(event: Event): void {
        event.preventDefault();
        this.openEditor();
        if (event.target instanceof Element && event.target.closest(".email-draft-subject-row")) {
            this.editorTarget.querySelector<HTMLInputElement>('[name="emailSubject"]')?.focus({ preventScroll: true });
        }
    }

    editFromKey(event: KeyboardEvent): void {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        event.preventDefault();
        this.openEditor();
    }

    showEmailCopy(event: Event): void {
        const button = event.currentTarget as HTMLButtonElement;
        const row = this.editorTarget.querySelector<HTMLElement>(`[data-email-copy-field="${button.dataset.emailCopyToggle}"]`);
        if (!row) return;
        row.hidden = false;
        button.hidden = true;
        button.setAttribute("aria-expanded", "true");
        row.querySelector<HTMLInputElement>(".email-recipients-input")?.focus();
    }

    cancel(event?: Event): void {
        event?.preventDefault();
        const form = this.editorTarget instanceof HTMLFormElement
            ? this.editorTarget
            : this.editorTarget.closest("form");
        form?.reset();
        const sender = form?.querySelector<HTMLElement & { value: string }>('[data-testid="ticket-action-email-sender-select"]');
        if (sender) sender.value = sender.dataset.initialValue ?? "";
        this.closeEditor();
    }

    submit(): void {
        this.closeEditor();
    }

    focusout(event: FocusEvent): void {
        const form = event.currentTarget;
        if (
            !(form instanceof HTMLFormElement)
            || form.dataset.ticketInlineEditSubmitOnBlur !== "true"
            || (event.relatedTarget instanceof Node && form.contains(event.relatedTarget))
        ) {
            return;
        }

        queueMicrotask(() => {
            if (!form.isConnected || form.classList.contains("hidden") || form.contains(document.activeElement)) {
                return;
            }

            form.requestSubmit();
        });
    }

    keydown(event: KeyboardEvent): void {
        if (event.defaultPrevented || event.isComposing) return;
        const target = event.target;
        if (event.key === "Enter"
            && target instanceof HTMLInputElement
            && target.name === "emailSubject"
            && this.editorTarget.matches('[data-controller~="email-autosave"]')) {
            event.preventDefault();
            const form = target.form;
            if (form?.reportValidity()) {
                this.editorTarget.dispatchEvent(new Event("email-editor:leave"));
                target.blur();
            }
            return;
        }
        if (event.key === "Escape") {
            if (this.editorTarget.matches('[data-controller~="email-autosave"]')) {
                this.editorTarget.dispatchEvent(new Event("email-editor:leave"));
                return;
            }
            this.cancel(event);
            return;
        }

        const form = event.currentTarget;
        const submitOnEnter = form instanceof HTMLFormElement
            && form.dataset.ticketInlineEditSubmitOnEnter === "true";
        if (event.key === "Enter" && (submitOnEnter || event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            if (form instanceof HTMLFormElement) {
                form.requestSubmit();
                if (submitOnEnter && form.checkValidity()) {
                    this.closeEditor();
                }
            }
        }
    }

    keepAssigneePopoverOpen(event: Event): void {
        if (event instanceof KeyboardEvent) {
            if (event.key === "Escape") {
                this.preserveAssigneePopover(false);
                return;
            }
            if (event.key !== "Enter" && event.key !== " ") {
                return;
            }
        }

        event.stopPropagation();
        this.preserveAssigneePopover(true);
    }

    syncAssigneePopoverState(event: Event): void {
        const dropdown = this.assigneeDropdown();
        if (!dropdown || !(event.target instanceof Node)) {
            return;
        }

        if (!dropdown.contains(event.target)) {
            this.preserveAssigneePopover(false);
            return;
        }

        queueMicrotask(() => {
            const trigger = dropdown.querySelector<HTMLElement>(":scope > button");
            const isOpen = trigger?.getAttribute("aria-expanded") === "true";
            this.preserveAssigneePopover(isOpen);
        });
    }

    submitAssignees(event: Event): void {
        const checkbox = event.currentTarget;
        if (!(checkbox instanceof HTMLInputElement)) {
            return;
        }

        this.preserveAssigneePopover(true);
        let checked = this.assigneeTargets.filter((input) => input.checked);
        if (checked.length === 0) {
            checkbox.checked = true;
            checked = [checkbox];
        }

        if (this.hasAssigneeIdsTarget) {
            this.assigneeIdsTarget.value = checked.map((input) => input.value).join(",");
        }

        const form = checkbox.closest("form");
        if (form instanceof HTMLFormElement) {
            form.requestSubmit();
        }
    }

    private assigneeDropdown(): HTMLElement | null {
        return this.element.closest<HTMLElement>(".dropdown-menu");
    }

    private assigneePopover(): HTMLElement | null {
        return this.assigneeDropdown()?.querySelector<HTMLElement>(":scope > [data-popover]") ?? null;
    }

    private preserveAssigneePopover(preserve: boolean): void {
        const popover = this.assigneePopover();
        if (!popover) {
            return;
        }

        if (preserve) {
            popover.setAttribute("hx-preserve", "true");
            popover.setAttribute("data-stream-preserve-self", "true");
            return;
        }

        popover.removeAttribute("hx-preserve");
        popover.removeAttribute("data-stream-preserve-self");
    }

    updateEmailSignature(): void {
        if (!this.hasSignatureFrameTarget || !this.hasEditorTarget) return;
        const mailAccount = this.editorTarget.querySelector<HTMLInputElement | HTMLSelectElement>('[name="mailAccountId"]')?.value;
        const signature = this.editorTarget.querySelector<HTMLInputElement | HTMLSelectElement>('[name="signatureUserId"]')?.value;
        const source = this.signatureSourceTargets.find((source) =>
            source.dataset.mailAccountId === mailAccount && source.dataset.signatureUserId === signature
        );
        const empty = this.editorTarget.querySelector<HTMLElement>('[data-email-signature-empty]');
        const missing = source?.dataset.empty === "true";
        this.signatureFrameTarget.parentElement!.hidden = missing;
        if (empty) {
            empty.hidden = !missing;
            empty.textContent = source?.dataset.emptyLabel ?? "";
        }
        const srcdoc = source?.dataset.srcdoc ?? "";
        if (this.signatureFrameTarget.srcdoc !== srcdoc) this.signatureFrameTarget.srcdoc = srcdoc;
    }

    private openEditor(): void {
        if (!this.hasDisplayTarget || !this.hasEditorTarget) {
            return;
        }

        this.displayTarget.classList.add("hidden");
        this.editorTarget.classList.remove("hidden");
        for (const row of this.editorTarget.querySelectorAll<HTMLElement>("[data-email-copy-field]")) {
            const empty = !row.querySelector<HTMLInputElement>('input[type="hidden"]')?.value.trim();
            row.hidden = empty;
            const toggle = this.editorTarget.querySelector<HTMLButtonElement>(`[data-email-copy-toggle="${row.dataset.emailCopyField}"]`);
            if (toggle) {
                toggle.hidden = !empty;
                toggle.setAttribute("aria-expanded", String(!empty));
            }
        }
        this.updateEmailSignature();
        this.editorTarget.dispatchEvent(new Event("email-editor:open"));
        const openClass = this.editorTarget.dataset.ticketInlineEditOpenClass;
        if (openClass) {
            this.editorTarget.classList.add(openClass);
        }
        if (this.inputTarget?.classList.contains("hidden")) {
            this.editorTarget.querySelector<HTMLElement>('[contenteditable="true"]')?.focus({ preventScroll: true });
        } else {
            this.inputTarget?.focus();
            this.inputTarget?.select();
        }
    }

    private closeEditor(): void {
        if (!this.hasDisplayTarget || !this.hasEditorTarget) {
            return;
        }

        const scrollPanel = this.element.closest<HTMLElement>("#tickets-detail-action-scroll");
        const scrollTop = scrollPanel?.scrollTop;
        const previewFrame = this.displayTarget.querySelector<HTMLIFrameElement>('[data-ticket-email-frame-target="frame"]');
        // A hidden, lazy iframe has not measured its new document yet. Reserve
        // space until it reports its real height, avoiding a temporary scroll clamp.
        if (previewFrame) previewFrame.style.height = `${this.editorTarget.offsetHeight}px`;
        const openClass = this.editorTarget.dataset.ticketInlineEditOpenClass;
        if (openClass) {
            this.editorTarget.classList.remove(openClass);
        }
        this.editorTarget.classList.add("hidden");
        this.displayTarget.classList.remove("hidden");
        // Switching back to the shorter preview must not anchor-scroll the ticket.
        if (scrollPanel && scrollTop !== undefined) scrollPanel.scrollTop = scrollTop;
    }
}
